// heal.ts — Janus C2b: the browser side of the PL heal (plan §3.4, §9 amendments 12/14/16). Step 1 shipped the primitives; step 2 adds the
// HEALER — the page-level state machine that decides WHICH roots to price and post, ONCE per (kind, root, generation), one in flight.
//   buildHealClaim(...)  — price the row AS HELD (no C1 clusters applied): the frozen claim bound to the stored row's identity.
//   postPlClaim(...)     — POST it to options/api/pl_claim_inc.php; the answer is a REASON, never an exception (failures are silent).
//   PlHealer             — needsHeal(row, mode) → dedupe by generation (contract + the row's C1 fingerprint, or a definition's revision
//                          token) → one POST at a time → backoff on retryable answers and on every `stale:*` (the dependencies refresh, the
//                          same claim is not re-sent) → a rate-limited answer pauses the whole drain → a changed generation (a WS delta
//                          rewrote the row) re-arms → an accepted identical write is silent. A completion only updates the record of the
//                          generation it ran for (Codex step-2 review A1): a newer generation that arrived meanwhile is never overwritten.
// Modes: 'unpriced' (the campaign roster: only a row with NO number or `pl_pending` is healed — a legacy server number stays until the
// compile flips, plan §3.5) and 'full' (the item page's own root: unpriced, pending, legacy-priced or priced under another contract).
import { buildPlClaim, plContractKey, plKindIsTokenBound, plRootIdentity } from './pl-claim';
import { plDependencyTotal as readDependencyPl } from './xref';
const plDependencyTotal = (arr: unknown): number | null => readDependencyPl(arr, plContractKey());
import type { PlClaimInputs } from './pl-claim';
import type { Row } from './read-context';
import { possessorInputFingerprintSource } from '../compile/possessor-compile';
import { backgroundCheckpoint, BackgroundSuperseded } from './background';
import { objectRequest } from '../../shared/object-request';
import { plStateOf, PL_SOURCE_BROWSER } from './pl-state';
export { plStateOf, PL_SOURCE_BROWSER, type PlState } from './pl-state';

export interface HealClaimInputs { row: Row; catalog: Row | null; store: PlClaimInputs['store']; rootId: string; kind: PlClaimInputs['kind']; budgetMs?: number; deadline?: number; checkpoint?: () => Promise<void> }
/** `blob_sig_b`: an inventory INSTANCE's claim names the compile it priced (the held copy's computed._cache.pl_sig_b) — its revision token
 *  does not cover the inventory row's own fields, so the server binds the write to that exact compile (pl_claim.php). */
export interface HealPost { root_id: string; kind: string; claim: Row; row_signature?: string; blob_sig_b?: string }
/** The signature carried by the compiled held copy, or null. */
export function plBlobSigB(row: Row | null | undefined): string | null {
    const cache = row && isObj(row.computed) && isObj((row.computed as Row)._cache) ? (row.computed as Row)._cache as Row : null;
    const s = cache?.pl_sig_b;
    return typeof s === 'string' && /^[0-9a-f]{40}$/i.test(s) ? s.toLowerCase() : null;
}
export interface HealAnswer { ok: boolean; written?: boolean; reason?: string; status: number; detail?: unknown; error?: string }
export type HealMode = 'unpriced' | 'full';

const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);

/** Fallback equips retain their existing pending entry until saved dependencies land. */
function equipmentPlPending(row: Row): boolean {
    const pending = typeof window === 'undefined' ? undefined : (window as unknown as {
        ftPendingEquips?: Map<string, { actorId: string; dependenciesPending?: boolean }>;
    }).ftPendingEquips;
    const { root_id } = plRootIdentity(row);
    return !!pending && [...pending.values()].some(entry => entry.actorId === root_id && entry.dependenciesPending);
}

export async function buildHealClaim(inputs: HealClaimInputs): Promise<Row> {
    if (equipmentPlPending(inputs.row)) return { unsupported: 'pl-equipment-pending' };
    // 8 s (was 3 s): a heal is never user-blocking, and a cold page must fetch every closure id WITH its token (C2b step-2 review 3)
    return buildWithWarmup({ prior: inputs.row, clusters: null, catalog: inputs.catalog, store: inputs.store, rootId: inputs.rootId, kind: inputs.kind, checkpoint: inputs.checkpoint }, inputs.deadline ?? performance.now() + (inputs.budgetMs ?? 8000));
}

/** List totals have no action waiting on them. After a budget refusal they may
 * join the closure already fetching, then rebuild once if it made progress.
 * Both builds retain the ordinary claim budget and exact root identity. */
export async function buildListHealClaim(inputs: HealClaimInputs, settlePending: (timeoutMs: number) => Promise<boolean>): Promise<Row> {
    const { store } = inputs, id = inputs.rootId.toUpperCase();
    const source = { def: store.definition(id), token: store.tokensFor([id])[id], epoch: store.epoch };
    const claim = await buildHealClaim(inputs);
    if (claim.unsupported !== 'pl-budget' || inputs.deadline !== undefined || source.def !== inputs.row) return claim;
    const progress = await settlePending(inputs.budgetMs ?? 8000);
    if (!progress || store.definition(id) !== source.def || store.tokensFor([id])[id] !== source.token || store.epoch !== source.epoch) return claim;
    const retry = await buildHealClaim(inputs);
    if (store.definition(id) !== source.def || store.tokensFor([id])[id] !== source.token || store.epoch !== source.epoch)
        return { entry_version: retry.entry_version, unsupported: 'pl-budget', diag: { warmup_root_changed: true } };
    return retry;
}

/** A timed-out fetch keeps warming the shared store. Join it once, then retry the unchanged engine only if a definition/refusal
 * actually arrived. This also handles a five-round closure that made progress in its last round, without changing that limit. */
async function buildWithWarmup(inputs: Omit<PlClaimInputs, 'deadline'>, deadline: number): Promise<Row> {
    if (performance.now() >= deadline) return { unsupported: 'pl-budget' };
    const base = inputs.store;
    const identity = plRootIdentity(inputs.prior), rootId = (inputs.rootId ?? identity.root_id).toUpperCase();
    const tokenBound = plKindIsTokenBound(inputs.kind ?? identity.kind);
    const source = { def: base.definition(rootId), token: base.tokensFor([rootId])[rootId], epoch: base.epoch, generation: storeGeneration(base) };
    const staleRoot = (): boolean => tokenBound && (base.definition(rootId) !== source.def || base.tokensFor([rootId])[rootId] !== source.token
        || base.epoch !== source.epoch || storeGeneration(base) !== source.generation);
    const staleClaim = (): Row => ({ entry_version: claim.entry_version, unsupported: 'pl-budget', diag: { warmup_root_changed: true } });
    const pending = new Set<Promise<unknown>>();
    const before = new Map<string, { def: Row | null; refusal: unknown }>();
    const store: PlClaimInputs['store'] = { ...base, get epoch() { return base.epoch; },
        definition: id => base.definition(id), refusal: id => base.refusal(id), tokensFor: ids => base.tokensFor(ids),
        ...(base.peek ? { peek: (id: string) => base.peek!(id) } : {}),
        getDefinitions: (ids, force) => {
            for (const id of ids) if (!before.has(id)) before.set(id, { def: base.definition(id), refusal: base.refusal(id) });
            const promise = base.getDefinitions(ids, force); pending.add(promise);
            void promise.finally(() => pending.delete(promise)).catch(() => undefined);
            return promise;
        },
    };
    let claim = await buildPlClaim({ ...inputs, store, deadline });
    if (performance.now() >= deadline) return { ...claim, value: undefined, unsupported: 'pl-budget' };
    if (claim.unsupported !== 'pl-budget' && claim.unsupported !== 'pl-closure-too-deep') return claim;
    // Warming can continue in the store, but a caller never joins it beyond THIS claim's deadline.
    if (pending.size && performance.now() < deadline) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        try { await Promise.race([Promise.allSettled([...pending]), new Promise<void>(r => { timer = setTimeout(r, Math.max(0, deadline - performance.now())); })]); }
        finally { if (timer) clearTimeout(timer); }
    }
    if (performance.now() >= deadline) return { ...claim, unsupported: 'pl-budget' };
    if ([...before].some(([id, old]) => base.definition(id) !== old.def || base.refusal(id) !== old.refusal)) {
        if (staleRoot()) return staleClaim();
        claim = await buildPlClaim({ ...inputs, store, deadline });
        if (staleRoot()) return staleClaim();
    }
    return claim;
}

// Rolling page-wide budget, shared across bundles/callers. Noops also consume quota.
// Dependencies may use 36 of the 48 slots, leaving 12 for roots (server limit: 60/min).
interface ClaimBudget { posts: Array<{ at: number; dependency: boolean }>; }
const budgetHost = globalThis as typeof globalThis & { __ftPlClaimBudget?: ClaimBudget };
export function takeClaimBudget(count: number, dependency: boolean, now = Date.now()): boolean {
    const state = budgetHost.__ftPlClaimBudget ??= { posts: [] };
    state.posts = state.posts.filter(p => p.at > now - 60_000);
    if (state.posts.length + count > 48 || (dependency && state.posts.filter(p => p.dependency).length + count > 36)) return false;
    for (let i = 0; i < count; i++) state.posts.push({ at: now, dependency });
    return true;
}
const yieldMain = (): Promise<void> => new Promise(resolve => setTimeout(resolve, 0));
export const PL_DEP_FAILURE_TTL_MS = 30_000;
interface FailureMemo { epoch: unknown; generation: unknown; failures: Map<string, { until: number; reason: string }>; }
const failureHost = globalThis as typeof globalThis & { __ftPlDependencyFailures?: WeakMap<object, FailureMemo> };
function dependencyFailures(store: HealClaimInputs['store']): FailureMemo['failures'] {
    const pages = failureHost.__ftPlDependencyFailures ??= new WeakMap();
    const owner = (store as { memoBase?: HealClaimInputs['store'] }).memoBase ?? store;
    let state = pages.get(owner);
    if (!state) {
        state = { epoch: store.epoch, generation: storeGeneration(store), failures: new Map() }; pages.set(owner, state);
        (owner as { onClear?: (fn: () => void) => void }).onClear?.(() => state!.failures.clear());
    }
    if (state.epoch !== store.epoch || state.generation !== storeGeneration(store)) {
        state.failures.clear(); state.epoch = store.epoch; state.generation = storeGeneration(store);
    }
    for (const [id, entry] of state.failures) if (entry.until <= Date.now()) state.failures.delete(id);
    return state.failures;
}
function failDependency(store: HealClaimInputs['store'], id: string, reason: string): void {
    dependencyFailures(store).set(id, { until: Date.now() + PL_DEP_FAILURE_TTL_MS, reason });
}

/** All PL/object transport shares the page's two slots. Timeouts cover body reads and start on send. */
async function claimRequest(base: string, post: unknown, timeout: number, count: number, dependency: boolean): Promise<{ status: number; body: Row }> {
    return objectRequest(async () => {
        if (!takeClaimBudget(count, dependency)) return { status: 429, body: { ok: false, reason: 'rate-limited' } };
        const ctl = new AbortController(), timer = setTimeout(() => ctl.abort(), timeout);
        try {
            const res = await fetch(base + 'options/api/pl_claim_inc.php', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(post), signal: ctl.signal });
            let body: Row = {};
            try { const value: unknown = await res.json(); if (isObj(value)) body = value; } catch { /* malformed response */ }
            return { status: res.status, body };
        } finally { clearTimeout(timer); }
    });
}
/** POST one claim, charging noops too. Never throws. */
export async function postPlClaim(base: string, post: HealPost, dependency = false): Promise<HealAnswer> {
    try {
        const { status, body } = await claimRequest(base, post, 8000, 1, dependency);
        return { ok: body.ok === true, written: body.written === true, reason: typeof body.reason === 'string' ? body.reason : undefined, status, detail: body.detail, error: typeof body.error === 'string' ? body.error : undefined };
    } catch (e) { return { ok: false, status: 0, error: e instanceof Error ? e.message : String(e) }; }
}
/** Batch claims share the same rolling quota; a reservation above the remaining budget sends nothing. */
export async function postPlClaimBatch(base: string, posts: HealPost[]): Promise<HealAnswer[]> {
    try {
        const { status, body } = await claimRequest(base, { claims: posts }, 60000, posts.length, false);
        const results = Array.isArray(body.results) ? body.results as Row[] : null;
        if (!results) return posts.map(() => ({ ok: false, status, error: typeof body.error === 'string' ? body.error : 'no-results' }));
        return posts.map((_, i) => { const r = results[i] ?? {}; return { ok: r.ok === true, written: r.written === true, reason: typeof r.reason === 'string' ? r.reason : undefined, status, detail: r.detail }; });
    } catch (e) { return posts.map(() => ({ ok: false, status: 0, error: e instanceof Error ? e.message : String(e) })); }
}

// ---- DEPENDENCY HEAL (owner 2026-10-10: "why isn't it re-priced when someone loads the page?") ----------------------------------------
// A price that reads ANOTHER item's stored PL (a summon's creature, a shapeshift form, an applied status, a granted ability, a charge
// grant, a choice option) refuses `pl-def-nopl:<id>` while that item has none — and after a Redis flush or a contract change every stored
// PL is gone at once, so a page could never price what it showed. The page now heals the dependency first: it prices and saves it as a
// DEFINITION root (recursively for its own dependencies), refetches it so the store holds the saved number, and prices again. Bounded per
// root (PL_DEP_MAX dependency heals, PL_DEP_DEPTH levels); a cycle or a refusal stops it and the root stays pending, as before.
// Brushstroke Monstrosity alone has 69 genuine creature choices (2026-10-20 census). Twelve could never recover its cold PL.
// Eighty covers that authored breadth plus nested calls; depth and the writer's independent rate limit stay bounded.
export const PL_DEP_MAX = 80;
export const PL_DEP_DEPTH = 4;
const NOPL = /^pl-def-nopl:([0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12})$/i;
/** The dependency a refused claim is waiting for, or null. */
export function plMissingDependency(claim: Row): string | null {
    const m = typeof claim.unsupported === 'string' ? NOPL.exec(claim.unsupported) : null;
    return m ? m[1]!.toUpperCase() : null;
}

// DISPLAY ONLY. A separate store facade supplies unsaved dependency prices to the existing xref reader. Neither the shared
// definitions nor their saved provenance is changed; a healer always sees the original store and must save its own dependencies.
interface ReadOnlyPrice { def: Row; epoch: unknown; generation: unknown; tokens: Record<string, string>; source?: Row; }
function pricedComputed(def: Row, pl: unknown, meta: unknown): Row {
    const computed: Row = { ...(isObj(def.computed) ? def.computed : {}), pl, pl_meta: meta };
    delete computed.pl_pending;
    return computed;
}
function priceOverlay(entry: ReadOnlyPrice, def: Row): Row {
    if (entry.source !== def) {
        const saved = isObj(entry.def.computed) ? entry.def.computed : {};
        entry.def = { ...def, _pl_stash: { verdict: 'hit' }, computed: pricedComputed(def, saved.pl, saved.pl_meta) };
        entry.source = def;
    }
    return entry.def;
}
interface ReadOnlyPrices { generation: unknown; values: Map<string, ReadOnlyPrice>; tail: Promise<unknown>; }
const readOnlyHost = globalThis as typeof globalThis & { __ftPlReadOnlyPrices?: WeakMap<object, ReadOnlyPrices> };
function readOnlyPrices(store: HealClaimInputs['store']): ReadOnlyPrices {
    const pages = readOnlyHost.__ftPlReadOnlyPrices ??= new WeakMap();
    let state = pages.get(store);
    if (!state) { state = { generation: storeGeneration(store), values: new Map(), tail: Promise.resolve() }; pages.set(store, state);
        (store as { onClear?: (fn: () => void) => void }).onClear?.(() => state!.values.clear()); }
    if (state.generation !== storeGeneration(store)) { state.values.clear(); state.generation = storeGeneration(store); }
    return state;
}
function storeGeneration(store: HealClaimInputs['store']): unknown { return (store as { cacheGeneration?: number }).cacheGeneration; }

/** Re-price without any claim writes, including for signed-in viewers opening a breakdown. Memoized per page/store, invalidated by
 * epoch, clear, or any transitive dependency token. All results are display_only and must never be submitted as heal claims. */
export async function pricePlReadOnly(inputs: HealClaimInputs): Promise<Row> {
    const state = readOnlyPrices(inputs.store);
    // One dependency traversal at a time: simultaneous roots cannot wait on one another's in-flight dependency, or spend each
    // other's bounds. Successful dependency values remain available to the next queued root.
    const job = state.tail.then(() => pricePlReadOnlyOnce({ ...inputs, deadline: inputs.deadline ?? performance.now() + (inputs.budgetMs ?? 8000) }, state));
    state.tail = job.catch(() => undefined);
    return job;
}

async function pricePlReadOnlyOnce(inputs: HealClaimInputs, state: ReadOnlyPrices): Promise<Row> {
    const base = inputs.store;
    const valid = (entry: ReadOnlyPrice): boolean => {
        if (entry.epoch !== base.epoch || entry.generation !== storeGeneration(base)) return false;
        const current = base.tokensFor(Object.keys(entry.tokens));
        return Object.entries(entry.tokens).every(([id, token]) => current[id] === token);
    };
    const used = new Set<string>();
    const lookup = (id: string): Row | null => {
        const key = id.toUpperCase(); const def = base.definition(key);
        if (!def || plDependencyTotal(def) !== null) return def;
        const entry = state.values.get(key);
        if (!entry) return def;
        if (!valid(entry)) { state.values.delete(key); return def; }
        used.add(key);
        return priceOverlay(entry, def);
    };
    // A fresh top-level read requested by this traversal need not be repeated in each warmed closure pass. A replaced object or
    // an invalidation re-arms it. The facade is private: normal forced refreshes in the shared store keep their original semantics.
    const refreshed = new Map<string, Row>();
    const store: HealClaimInputs['store'] = {
        get epoch() { return base.epoch; }, get cacheGeneration() { return storeGeneration(base) as number; }, definition: lookup, refusal: id => base.refusal(id), tokensFor: ids => base.tokensFor(ids),
        ...(base.peek ? { peek: (id: string) => base.peek!(id) } : {}),
        getDefinitions: async (ids, force = false) => {
            const topLevel = base as { isTopLevel?: (id: string) => boolean };
            const wanted = force ? ids.filter(id => {
                const def = base.definition(id);
                if (!def || !base.tokensFor([id])[id.toUpperCase()]) return true;
                return !topLevel.isTopLevel?.(id) && refreshed.get(id.toUpperCase()) !== def;
            }) : ids;
            if (wanted.length) await base.getDefinitions(wanted, force);
            const out = new Map<string, { id: string; def: Row; revision_token: string | null }>();
            for (const raw of ids) {
                const id = raw.toUpperCase(), def = base.definition(id);
                if (def) { if (force) refreshed.set(id, def); out.set(id, { id, def, revision_token: base.tokensFor([id])[id] ?? null }); }
            }
            return out;
        },
    };
    const build = async (row: Row, rootId: string, kind: HealClaimInputs['kind'], catalog: Row | null): Promise<Row> => {
        const claim = await buildWithWarmup({ prior: row, clusters: null, catalog, store,
            rootId, kind, includeBreakdown: true, checkpoint: inputs.checkpoint }, inputs.deadline!);
        return { ...claim, display_only: true };
    };
    const run = { budget: PL_DEP_MAX, visiting: new Set([inputs.rootId.toUpperCase()]) };
    let failure: string | undefined;
    const dependency = async (id: string, depth: number): Promise<boolean> => {
        if (performance.now() >= inputs.deadline!) { failure = 'pl-budget'; return false; }
        const negative = dependencyFailures(base).get(id);
        if (negative) { failure = negative.reason; return false; }
        if (run.visiting.has(id)) { failure = 'pl-dependency-cycle:' + id; return false; }
        if (depth > PL_DEP_DEPTH) { failure = 'pl-dependency-depth:' + id; return false; }
        if (plDependencyTotal(lookup(id)) !== null) return true;
        if (run.budget <= 0) { failure = 'pl-dependency-limit:' + id; return false; }
        run.budget--; run.visiting.add(id);
        try {
            try { await store.getDefinitions([id], true); } catch { failure = 'pl-dependency-fetch:' + id; failDependency(base, id, failure); return false; }
            const def = base.definition(id);
            if (!def) { failure = 'pl-def-' + (base.refusal(id) ?? 'missing') + ':' + id; failDependency(base, id, failure); return false; }
            if (plDependencyTotal(def) !== null) return true;
            const epoch = base.epoch, generation = storeGeneration(base), token = base.tokensFor([id])[id];
            let claim = await build(def, id, 'item', isObj(def.catalog) ? def.catalog : null);
            for (let next = plMissingDependency(claim); next; next = plMissingDependency(claim)) {
                if (!await dependency(next, depth + 1)) return false;
                await yieldMain();
                claim = await build(def, id, 'item', isObj(def.catalog) ? def.catalog : null);
            }
            if (typeof claim.value !== 'number' || !Number.isFinite(claim.value)) { failure = String(claim.unsupported ?? 'pl-dependency-error:' + id); failDependency(base, id, failure); return false; }
            if (base.definition(id) !== def || base.epoch !== epoch || storeGeneration(base) !== generation || base.tokensFor([id])[id] !== token) {
                failure = 'pl-dependency-stale:' + id; return false;
            }
            const tokens = { ...(isObj(claim.closure) ? claim.closure as Record<string, string> : {}) };
            for (const key of used) { const cached = state.values.get(key); if (cached && valid(cached)) Object.assign(tokens, cached.tokens); }
            // A display claim may have an incomplete token map; never memoize an unbound transitive read.
            if (!tokens[id] || (typeof claim.closure_size === 'number' && Object.keys(claim.closure as Row).length !== claim.closure_size)) { failure = 'pl-dependency-untokened:' + id; return false; }
            state.values.set(id, { def: { ...def, computed: pricedComputed(def, claim.value, { source: 'browser-computed-unsaved', contract: plContractKey() }) }, tokens, epoch: base.epoch, generation: storeGeneration(base) });
            return true;
        } finally { run.visiting.delete(id); }
    };
    let claim = await build(inputs.row, inputs.rootId, inputs.kind, inputs.catalog);
    for (let id = plMissingDependency(claim); id; id = plMissingDependency(claim)) {
        if (!await dependency(id, 1)) return { ...claim, ...(failure === 'pl-budget' ? { unsupported: 'pl-budget' } : {}), ...(failure ? { dependency_refusal: failure } : {}) };
        await yieldMain();
        claim = await build(inputs.row, inputs.rootId, inputs.kind, inputs.catalog);
    }
    return claim;
}
interface DepRun { budget: number; visiting: Set<string>; healed: string[]; remember(id: string, claim: Row): boolean; answer?: HealAnswer; failure?: string }
// Separate from unsaved display prices: these values have an actual written/noop receipt.
// A temporarily sparse refetch can use that acknowledged value. Never use display memos for a claim.
const confirmedHost = globalThis as typeof globalThis & { __ftPlConfirmedPrices?: WeakMap<object, Map<string, ReadOnlyPrice>> };
function confirmedStore(base: HealClaimInputs['store']): { store: HealClaimInputs['store']; remember(id: string, claim: Row): boolean } {
    const pages = confirmedHost.__ftPlConfirmedPrices ??= new WeakMap();
    let values = pages.get(base); if (!values) { values = new Map(); pages.set(base, values);
        (base as { onClear?: (fn: () => void) => void }).onClear?.(() => values!.clear()); }
    const valid = (entry: ReadOnlyPrice): boolean => entry.epoch === base.epoch && entry.generation === storeGeneration(base)
        && Object.entries(entry.tokens).every(([id, token]) => base.tokensFor([id])[id] === token);
    const store = { memoBase: base, get epoch() { return base.epoch; }, get cacheGeneration() { return storeGeneration(base); },
        refusal: (id: string) => base.refusal(id), tokensFor: (ids: readonly string[]) => base.tokensFor(ids),
        getDefinitions: base.getDefinitions.bind(base), onClear: (base as { onClear?: (fn: () => void) => void }).onClear?.bind(base), ...(base.peek ? { peek: base.peek.bind(base) } : {}),
        definition: (id: string): Row | null => {
            const def = base.definition(id); if (!def || plDependencyTotal(def) !== null) return def;
            const cached = values!.get(id.toUpperCase()); if (!cached) return def;
            if (!valid(cached)) { values!.delete(id.toUpperCase()); return def; }
            return priceOverlay(cached, def);
        },
    };
    return { store, remember: (id, claim) => {
        const def = base.definition(id), tokens = isObj(claim.closure) ? claim.closure as Record<string, string> : {};
        if (!def || typeof claim.value !== 'number' || !tokens[id] || claim.epoch !== base.epoch) return false;
        const entry: ReadOnlyPrice = { def: { ...def, _pl_stash: { verdict: 'hit' }, computed: pricedComputed(def, claim.value, { source: PL_SOURCE_BROWSER, contract: plContractKey(), status_share: claim.status_share, total_gp: claim.total_gp }) },
            source: def, tokens, epoch: base.epoch, generation: storeGeneration(base) };
        if (!valid(entry)) return false;
        values!.set(id, entry); return true;
    } };
}
// a rate-limited dependency write pauses EVERY dependency heal on the page for a minute: the viewer's claim quota (60/min) is shared with the
// page's own roots, which must not be starved by dependency traffic (Codex review 2026-10-10)
const depPauseHost = globalThis as typeof globalThis & { __ftPlDepPausedUntil?: number };
async function healDependency(base: string, inputs: HealClaimInputs, id: string, depth: number, run: DepRun): Promise<boolean> {
    const failed = dependencyFailures(inputs.store).get(id);
    if (performance.now() >= inputs.deadline!) { run.failure = 'pl-budget'; return false; }
    if (failed) { run.failure = failed.reason; return false; }
    if (depth > PL_DEP_DEPTH || run.budget <= 0 || run.visiting.has(id) || Date.now() < (depPauseHost.__ftPlDepPausedUntil ?? 0)) return false;
    run.budget--; run.visiting.add(id);
    try {
        try { await inputs.store.getDefinitions([id], true); } catch { failDependency(inputs.store, id, 'pl-dependency-fetch:' + id); return false; } // top-level: the token + a compiled blob to write into
        for (;;) {
            const def = inputs.store.definition(id) as Row | null;
            if (!def) { failDependency(inputs.store, id, 'pl-def-' + (inputs.store.refusal(id) ?? 'missing') + ':' + id); return false; }
            // A current wire value is read, never posted again (including a zero PL).
            if (plDependencyTotal(def) !== null) return true;
            await yieldMain();
            const claim = await buildHealClaim({ row: def, catalog: isObj(def.catalog) ? def.catalog : null, store: inputs.store, rootId: id, kind: 'item', budgetMs: inputs.budgetMs, deadline: inputs.deadline, checkpoint: inputs.checkpoint });
            const next = plMissingDependency(claim);
            if (next) { if (!await healDependency(base, inputs, next, depth + 1, run)) return false; continue; }
            if (typeof claim.value !== 'number') { run.failure = String(claim.unsupported ?? 'error'); failDependency(inputs.store, id, run.failure); return false; }
            if (inputs.checkpoint) await inputs.checkpoint();
            const answer = await postPlClaim(base, { root_id: id, kind: 'item', claim, ...(typeof claim.row_signature === 'string' ? { row_signature: claim.row_signature } : {}) }, true);
            if (answer.reason === 'rate-limited') { depPauseHost.__ftPlDepPausedUntil = Date.now() + 60_000; run.answer = answer; }
            if (answer.reason !== 'written' && answer.reason !== 'noop') { if (answer.reason !== 'rate-limited') failDependency(inputs.store, id, 'pl-dependency-write:' + (answer.reason ?? 'network')); return false; }
            // healed only when the store copy now really carries the saved number (else the root would refuse on it again)
            try { await inputs.store.getDefinitions([id], true); } catch { return false; }
            if (plDependencyTotal(inputs.store.definition(id)) === null) {
                if (!run.remember(id, claim)) return false;
            }
            if (plDependencyTotal(inputs.store.definition(id)) === null) return false;
            run.healed.push(id);
            return true;
        }
    } finally { run.visiting.delete(id); }
}

/** Build + post for a row held on the page. Returns the claim and the answer (the smoke's receipt; the healer reads `answer.reason`). */
/** `dependencies`: the definitions this heal priced + saved first (their stored PL was missing). */
export async function healPlOnce(base: string, inputs: HealClaimInputs): Promise<{ claim: Row; answer: HealAnswer | null; dependencies?: string[] }> {
    // an instance's compile signature is captured BEFORE pricing awaits: the price and the signature it is bound to come from the same
    // held row version (a copy updated in place during the build must not lend its newer signature to an older price)
    inputs = { ...inputs, deadline: inputs.deadline ?? performance.now() + (inputs.budgetMs ?? 8000) };
    const sigBAtPrice = plBlobSigB(inputs.row);
    const confirmed = confirmedStore(inputs.store);
    inputs = { ...inputs, store: confirmed.store };
    let claim = await buildHealClaim(inputs);
    // a refusal on a dependency with no stored PL: heal the dependency (bounded), then price again
    const run: DepRun = { budget: PL_DEP_MAX, visiting: new Set([inputs.rootId.toUpperCase()]), healed: [], remember: confirmed.remember };
    for (let dep = plMissingDependency(claim); dep; dep = plMissingDependency(claim)) {
        if (!await healDependency(base, inputs, dep, 1, run)) break;
        await yieldMain();
        claim = await buildHealClaim(inputs);
    }
    if (typeof claim.value !== 'number' && run.failure === 'pl-budget') claim = { ...claim, unsupported: 'pl-budget' };
    const deps = run.healed.length ? { dependencies: run.healed } : {};
    if (typeof claim.value !== 'number') return { claim, answer: run.answer ?? null, ...deps }; // a refusal is not posted
    // the POST names the ROOT the claim itself was built for (the claim's kind/root_id, never a caller default)
    const kind = typeof claim.kind === 'string' ? claim.kind : (inputs.kind ?? 'encounter_creature');
    const sigB = kind === 'inventory' ? sigBAtPrice : null;
    // never an unbound instance write: a client-side refusal, not a post (answer null; the drain counts it as refused)
    if (kind === 'inventory' && !sigB) return { claim: { ...claim, value: undefined, unsupported: 'no-blob-sig' }, answer: null, ...deps };
    if (inputs.checkpoint) await inputs.checkpoint();
    if (equipmentPlPending(inputs.row)) return { claim: { unsupported: 'pl-equipment-pending' }, answer: null, ...deps };
    const answer = await postPlClaim(base, { root_id: typeof claim.root_id === 'string' ? claim.root_id : inputs.rootId, kind, claim, ...(typeof claim.row_signature === 'string' ? { row_signature: claim.row_signature } : {}), ...(sigB ? { blob_sig_b: sigB } : {}) });
    return { claim, answer, ...deps };
}

/** Does this row need a heal in this mode? */
export function needsHeal(row: Row, mode: HealMode, contractKey: string = plContractKey()): { need: boolean; why: string } {
    const s = plStateOf(row);
    if (s.value === null) return { need: true, why: 'unpriced' };
    if (s.pending) return { need: true, why: 'pending' };
    if (mode === 'unpriced') return { need: false, why: 'priced' };
    if (s.source !== 'browser-frozen') return { need: true, why: 'legacy' };
    if (s.contract !== contractKey) return { need: true, why: 'contract' };
    return { need: false, why: 'current' };
}

// ---------------------------------------------------------------------------------------------------------------------
// The healer: per-page dedupe by (kind, root, generation), one POST in flight, backoff, re-arm on a new generation.
// ---------------------------------------------------------------------------------------------------------------------
/** `force`: re-price once for this token even when the row reads current (C-PI: a passive-integer fold changed values PL prices; the token is the fold state, so each state re-prices once per page — the server answers noop when the number did not move). */
export interface HealTarget { row: Row; catalog: Row | null; rootId?: string; kind?: PlClaimInputs['kind']; mode: HealMode; force?: string }
export interface HealRecord { generation: string; state: 'inflight' | 'done' | 'retry'; reason: string; at: number; retryAt?: number; attempts?: number }
export interface HealerDeps { base: string; store: PlClaimInputs['store']; now?: () => number; run?: (base: string, inputs: HealClaimInputs) => Promise<{ claim: Row; answer: HealAnswer | null; dependencies?: string[] }> }
interface HealJob { target: HealTarget; rootId: string; kind: NonNullable<PlClaimInputs['kind']>; key: string; generation: string }
const RETRY_REASONS = new Set(['retry', 'no-redis', 'error', 'network', 'rate-limited', 'unavailable', 'not-posted']);
export const HEAL_RETRY_AFTER_MS = 30_000;
/** Backoff per attempt of ONE generation: 30 s → 2 min → 10 min → 10 min; the 5th failure settles the generation as `gave-up:<reason>`. */
export const HEAL_BACKOFF_MS = [30_000, 120_000, 600_000, 600_000];
export const HEAL_MAX_ATTEMPTS = 5;
export const HEAL_RATE_LIMIT_PAUSE_MS = 60_000;

async function sha256Hex(text: string): Promise<string> {
    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
}

export class PlHealer {
    readonly records = new Map<string, HealRecord>();
    private readonly considering = new Set<string>();
    private readonly forceWaiting = new Map<string, HealTarget>();
    /** The last forced re-prices' outcomes (C-PI fold states), newest last, capped at 32. */
    readonly forced: Array<{ key: string; token: string; reason: string; at: number }> = [];
    /** The last client-side refusals (a claim that was never posted) with the claim's diagnostics, newest last, capped at 16. */
    readonly refusals: Array<{ key: string; reason: string; diag: unknown; at: number }> = [];
    private queue: HealJob[] = [];
    private draining = false;
    /** The drain is paused until this time after a rate-limited answer (the limit is per viewer, shared by every root and tab). */
    pausedUntil = 0;
    readonly stats = { posted: 0, written: 0, noop: 0, skipped: 0, refused: 0, retried: 0, paused: 0, appliedLocal: 0, dependenciesHealed: 0 };
    constructor(private readonly deps: HealerDeps) {}
    private now(): number { return this.deps.now ? this.deps.now() : Date.now(); }

    /** The generation of a row = the contract it will be priced under + the identity the claim will carry (rows: root|fingerprint;
     *  definitions and instances: the revision token, fetched through the store FIRST so the identity never changes between two considerations). */
    async generationOf(row: Row, rootId: string, kind: string): Promise<string> {
        const contract = plContractKey();
        if (plKindIsTokenBound(kind)) {
            let tok = this.deps.store.tokensFor([rootId])[rootId];
            // a copy held WITHOUT a token (interned from another payload's nested catalog) must be re-fetched top-level: force
            if (!tok) { try { await this.deps.store.getDefinitions([rootId], true); } catch { /* the claim build refuses on its own */ } tok = this.deps.store.tokensFor([rootId])[rootId]; }
            // an instance's generation also names the compile (a row-field edit recompiles it without moving its token)
            if (tok) return contract + '|tok:' + tok + (kind === 'inventory' ? '|sb:' + (plBlobSigB(row) ?? '-') : '');
        }
        return contract + '|' + await sha256Hex(rootId + '|' + possessorInputFingerprintSource(row));
    }

    /** Consider a target: enqueue when it needs a heal and no record already covers this generation. Returns why it was or was not queued. */
    async consider(target: HealTarget): Promise<string> {
        if (equipmentPlPending(target.row)) return 'skip:equipment-pending';
        const identity = plRootIdentity(target.row);
        const rootId = (target.rootId ?? identity.root_id).toUpperCase();
        const kind = target.kind ?? identity.kind;
        if (!rootId) return 'no-root';
        const need = target.force ? { need: true, why: 'forced' } : needsHeal(target.row, target.mode);
        if (!need.need) { this.stats.skipped++; return 'skip:' + need.why; }
        const key = kind + ':' + rootId;
        // one consideration per root at a time: generationOf() awaits a token fetch, and two scans arriving inside that window must
        // not both queue the same generation
        // …but a FORCED request is not lost (Codex review 2026-09-27): it waits and re-enters once the running consideration ends. A newer
        // force for the same root replaces an older waiting one — only the latest fold state needs a price (Codex round 2, deliberately kept)
        if (this.considering.has(key)) { if (target.force) this.forceWaiting.set(key, target); return 'skip:considering'; }
        this.considering.add(key);
        let generation: string;
        try { generation = await this.generationOf(target.row, rootId, kind) + (target.force ? '|f:' + target.force : ''); } finally {
            this.considering.delete(key);
            const waiting = this.forceWaiting.get(key);
            if (waiting) { this.forceWaiting.delete(key); if (waiting !== target) queueMicrotask(() => { void this.consider(waiting).catch(() => undefined); }); }
        }
        const rec = this.records.get(key);
        if (rec && rec.generation === generation) {
            if (rec.state === 'inflight') return 'skip:inflight';
            // a settled generation whose held copy has since lost its number (a resync replaced the row object) re-claims once: the answer
            // is noop, and the local apply below repaints the copy (step-3 review A2)
            if (rec.state === 'done' && !(['written', 'noop', 'ws'].includes(rec.reason) && plStateOf(target.row).value === null)) return 'skip:done:' + rec.reason;
            if (rec.state === 'retry' && (rec.retryAt ?? 0) > this.now()) return 'skip:backoff';
        }
        // a newer generation REPLACES a queued (not yet running) job for the same root; a running one finishes for its own generation
        const queued = this.queue.findIndex(j => j.key === key);
        if (queued >= 0) { if (this.queue[queued]!.generation === generation) return 'skip:queued'; this.queue.splice(queued, 1); }
        this.records.set(key, { generation, state: 'inflight', reason: need.why, at: this.now(), attempts: rec && rec.generation === generation ? (rec.attempts ?? 0) : 0 });
        this.queue.push({ target, rootId, kind, key, generation });
        void this.drain();
        return 'queued:' + need.why;
    }

    /** One POST at a time; the answer decides the record — of the generation the job ran for, never a newer one. */
    private async drain(): Promise<void> {
        if (this.draining) return;
        this.draining = true;
        try {
            while (this.queue.length) {
                await backgroundCheckpoint();
                if (this.pausedUntil > this.now()) { // rate-limited: park every queued job as retry, stop draining
                    for (const j of this.queue.splice(0)) this.settle(j, 'rate-limited');
                    break;
                }
                const job = this.queue.shift()!;
                let reason = 'error';
                let lastClaim: Row | null = null;
                try {
                    const checkpoint = async (): Promise<void> => {
                        await backgroundCheckpoint();
                        if (this.records.get(job.key)?.generation !== job.generation) throw new BackgroundSuperseded();
                    };
                    const run = this.deps.run ?? healPlOnce;
                    const out = await run(this.deps.base, { row: job.target.row, catalog: job.target.catalog, store: this.deps.store, rootId: job.rootId, kind: job.kind, checkpoint });
                    lastClaim = out.claim;
                    this.stats.dependenciesHealed += out.dependencies?.length ?? 0;
                    if (typeof out.claim.value !== 'number') {
                        reason = out.answer?.reason === 'rate-limited' ? 'rate-limited' : 'refused:' + String(out.claim.unsupported ?? (isObj(out.claim.error) ? 'error' : 'unknown')); this.stats.refused++;
                        this.refusals.push({ key: job.key, reason, diag: out.claim.diag ?? out.claim.error ?? null, at: this.now() }); if (this.refusals.length > 16) this.refusals.shift();
                    }
                    else { this.stats.posted++; reason = out.answer ? (out.answer.reason ?? (out.answer.status === 0 ? 'network' : 'http:' + out.answer.status)) : 'not-posted'; if (reason === 'written') this.stats.written++; if (reason === 'noop') this.stats.noop++; }
                } catch (e) { reason = e instanceof BackgroundSuperseded ? 'superseded' : 'error:' + (e instanceof Error ? e.message : String(e)).slice(0, 80); }
                if (reason === 'rate-limited') { this.pausedUntil = this.now() + HEAL_RATE_LIMIT_PAUSE_MS; this.stats.paused++; }
                if ((reason === 'written' || reason === 'noop') && lastClaim) await this.applyLocal(job, lastClaim);
                this.settle(job, reason);
            }
        } finally { this.draining = false; }
    }

    /** The server holds this number for this generation (written or noop): the page's copy shows it too — provided the copy is still that
     *  generation (a row mutated since the claim was built is left to its own heal). */
    private async applyLocal(job: HealJob, claim: Row): Promise<void> {
        try {
            if (typeof claim.value !== 'number' || await this.generationOf(job.target.row, job.rootId, job.kind) !== job.generation) return;
            const row = job.target.row;
            const computed: Row = isObj(row.computed) ? row.computed : (row.computed = {} as Row) as Row;
            computed.pl = claim.value;
            computed.pl_meta = { source: PL_SOURCE_BROWSER, contract: plContractKey(), status_share: typeof claim.status_share === 'number' ? claim.status_share : null, total_gp: claim.total_gp };
            if (isObj(claim.sep) && Object.keys(claim.sep).length) computed.status_effect_permanent_pl = claim.sep; else delete computed.status_effect_permanent_pl;
            delete computed.pl_pending;
            this.stats.appliedLocal++;
            // announce the saved price: rendered pending pills for this root repaint in place (object-card/render-object-card.ts)
            const g = globalThis as { dispatchEvent?: (e: Event) => boolean; CustomEvent?: typeof CustomEvent };
            if (typeof g.dispatchEvent === 'function' && typeof g.CustomEvent === 'function') {
                try { g.dispatchEvent(new g.CustomEvent('ft:pl-healed', { detail: { rootId: job.rootId, kind: job.kind, value: claim.value } })); } catch { /* display only */ }
            }
        } catch { /* a copy that cannot be re-identified stays as it is */ }
    }

    private settle(job: HealJob, reason: string): void {
        // a forced (fold-state) re-price keeps its outcome even after a later ordinary generation replaces the record
        const fi = job.generation.indexOf('|f:');
        if (fi >= 0) { this.forced.push({ key: job.key, token: job.generation.slice(fi + 3), reason, at: this.now() }); if (this.forced.length > 32) this.forced.shift(); }
        const head = reason.split(':')[0]!;
        // retryable: transport / server-side transient / every stale:* (the dependencies refresh; the same claim is not re-sent before the
        // backoff) / a pl-budget refusal (the closure is still warming)
        const retry = RETRY_REASONS.has(head) || reason.startsWith('http:') || reason.startsWith('stale:') || reason.startsWith('refused:pl-budget');
        if (retry) this.stats.retried++;
        const cur = this.records.get(job.key);
        if (cur && cur.generation !== job.generation) return; // a newer generation owns the record now (Codex step-2 review A1)
        // A stale answer names what moved: refresh it BEFORE the retry, or the same claim is resent forever (C2b step-2 review 2).
        // `stale:epoch` → one forced fetch of the root observes the newer epoch (the store drops and rebuilds); `stale:def:<id>` → that id.
        if (reason === 'stale:epoch' || reason === 'stale:contract') void this.deps.store.getDefinitions([job.rootId], true).catch(() => undefined);
        const staleDef = /^stale:def:([0-9A-F-]{36})$/i.exec(reason);
        if (staleDef) void this.deps.store.getDefinitions([staleDef[1]!], true).catch(() => undefined);
        const attempts = (cur && cur.generation === job.generation ? (cur.attempts ?? 0) : 0) + (retry ? 1 : 0);
        if (retry && attempts >= HEAL_MAX_ATTEMPTS) { this.records.set(job.key, { generation: job.generation, state: 'done', reason: 'gave-up:' + reason, at: this.now(), attempts }); return; }
        const wait = HEAL_BACKOFF_MS[Math.min(Math.max(attempts - 1, 0), HEAL_BACKOFF_MS.length - 1)]!;
        this.records.set(job.key, { generation: job.generation, state: retry ? 'retry' : 'done', reason, at: this.now(), attempts, ...(retry ? { retryAt: this.now() + wait } : {}) });
    }

    /** A snapshot for the smoke / console. */
    snapshot(): Record<string, HealRecord> { return Object.fromEntries(this.records); }
}
